import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import { get, isNumber } from "lodash";
import PropTypes from "prop-types";

import Collapse from "./Collapse";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

const GeneralInformationView = ({
  applicationData,
  isExpandable = true,
  isTracking = false,
  t,
}) => {
  const {
    fireSafetyAddress,
    fireSafetyCertificate,
    occupancyType,
    fireSafetyPremiseType,
    numberOfBlock,
    buildingHeight,
    mainEntranceWidth,
    totalFloorArea,
    buildUpArea,
    numberOfFloor,
    frontClearSpace,
    backClearSpace,
    rightClearSpace,
    leftClearSpace,
    mentionObstructions,
    buildingNumber,
  } = applicationData;

  const {
    premiseArea,
    zone,
    block,
    building,
    nearestLandmark,
    floors,
    pinCode,
    city,
  } = fireSafetyAddress || {};

  const { approvalNumber, dateOfIssue, dueDate } = fireSafetyCertificate || {};

  const fields = useMemo(
    () => [
      {
        name: "Address",
        header: true,
        isVisible: isTracking
          ? !!premiseArea?.name ||
            !!zone?.name ||
            !!block?.name ||
            !!building?.name ||
            !!buildingNumber ||
            !!nearestLandmark ||
            !!floors?.length ||
            !!pinCode ||
            !!city?.name
          : true,
      },
      {
        name: "Premise Area",
        key: "fireSafetyAddress.premiseArea.name",
        isRequired: true,
        isVisible: isTracking ? !!premiseArea?.name : true,
      },
      {
        name: "Zone Name",
        key: "fireSafetyAddress.zone.name",
        isRequired: true,
        isVisible: isTracking ? !!zone?.name : true,
      },
      {
        name: "Block Name",
        key: "fireSafetyAddress.block.name",
        isRequired: true,
        isVisible: isTracking ? !!block?.name : true,
      },
      {
        name: "Building Name",
        key: "fireSafetyAddress.building.name",
        isRequired: true,
        isVisible: isTracking ? !!building?.name : true,
      },
      {
        name: "Building Number",
        key: "buildingNumber",
        isRequired: false,
        isVisible: isTracking ? !!buildingNumber : true,
      },
      {
        name: "Nearest Landmark",
        key: "fireSafetyAddress.nearestLandmark",
        isRequired: false,
        isVisible: isTracking ? !!nearestLandmark : true,
      },
      {
        name: "Floors",
        key: "fireSafetyAddress.floors",
        renderField: (floors) =>
          floors.length ? floors.map(({ name }) => name).join(", ") : "N/A",
        isVisible: isTracking ? !!floors?.length : true,
      },
      {
        name: "Pincode",
        key: "fireSafetyAddress.pinCode",
        isRequired: true,
        isVisible: isTracking ? !!pinCode : true,
      },

      {
        name: "City",
        key: "fireSafetyAddress.city.name",
        isRequired: true,
        isVisible: isTracking ? !!city?.name : true,
      },
      {
        name: "Fire Safety Certificate Approval",
        header: true,
        isVisible: isTracking
          ? !!approvalNumber || !!dateOfIssue || !!dueDate
          : true,
      },
      {
        name: "Approval Number",
        key: "fireSafetyCertificate.approvalNumber",
        isRequired: true,
        isVisible: isTracking ? !!approvalNumber : true,
      },
      {
        name: "Date of Issue",
        key: "fireSafetyCertificate.dateOfIssue",
        renderField: (value) =>
          value ? convertUTCDateToLocalDate(value).format("DD-MM-YYYY") : "-",
        isVisible: isTracking ? !!dateOfIssue : true,
      },
      {
        name: "Due Date",
        key: "fireSafetyCertificate.dueDate",
        renderField: (value) =>
          value ? convertUTCDateToLocalDate(value).format("DD-MM-YYYY") : "-",
        isVisible: isTracking ? !!dueDate : true,
      },
      {
        name: "Building Information",
        header: true,
        isVisible: isTracking
          ? !!occupancyType?.name ||
            !!numberOfBlock ||
            !!numberOfFloor ||
            isNumber(buildingHeight) ||
            isNumber(mainEntranceWidth) ||
            isNumber(totalFloorArea) ||
            isNumber(buildUpArea) ||
            isNumber(frontClearSpace) ||
            isNumber(backClearSpace) ||
            isNumber(rightClearSpace) ||
            isNumber(leftClearSpace) ||
            !!mentionObstructions
          : true,
      },
      {
        name: "Occupancy Type",
        key: "occupancyType.name",
        isRequired: true,
        isVisible: isTracking ? !!occupancyType?.name : true,
      },
      {
        name: "Nature Of Building",
        key: "fireSafetyPremiseType.name",
        isRequired: true,
        isVisible: isTracking ? !!fireSafetyPremiseType?.name : true,
      },
      {
        name: "Number of Blocks",
        key: "numberOfBlock",
        isRequired: true,
        isVisible: isTracking ? !!numberOfBlock : true,
      },
      {
        name: "Building Height (Mtrs)",
        key: "buildingHeight",
        isRequired: true,
        isVisible: isTracking ? isNumber(buildingHeight) : true,
      },
      {
        name: "Main Entrance Width (Mtrs)",
        key: "mainEntranceWidth",
        isRequired: true,
        isVisible: isTracking ? isNumber(mainEntranceWidth) : true,
      },
      {
        name: "Total Floor Area (Sq.Mtrs)",
        key: "totalFloorArea",
        isRequired: true,
        isVisible: isTracking ? isNumber(totalFloorArea) : true,
      },
      {
        name: "Build Up Area (Sq.Mtrs)",
        key: "buildUpArea",
        isRequired: true,
        isVisible: isTracking ? isNumber(buildUpArea) : true,
      },
      {
        name: "Number of Floors",
        key: "numberOfFloor",
        isRequired: true,
        isVisible: isTracking ? !!numberOfFloor : true,
      },
      {
        name: "Front Clear Space (Mtrs)",
        key: "frontClearSpace",
        isRequired: true,
        isVisible: isTracking ? isNumber(frontClearSpace) : true,
      },
      {
        name: "Back Clear Space (Mtrs)",
        key: "backClearSpace",
        isRequired: true,
        isVisible: isTracking ? isNumber(backClearSpace) : true,
      },
      {
        name: "Right Clear Space (Mtrs)",
        key: "rightClearSpace",
        isRequired: true,
        isVisible: isTracking ? isNumber(rightClearSpace) : true,
      },
      {
        name: "Left Clear Space (Mtrs)",
        key: "leftClearSpace",
        isRequired: true,
        isVisible: isTracking ? isNumber(leftClearSpace) : true,
      },
      {
        name: "Mention Obstruction",
        key: "mentionObstructions",
        isRequired: false,
        isVisible: isTracking ? !!mentionObstructions : true,
      },
    ],
    [
      occupancyType,
      numberOfBlock,
      buildingHeight,
      mainEntranceWidth,
      totalFloorArea,
      buildUpArea,
      numberOfFloor,
      frontClearSpace,
      backClearSpace,
      rightClearSpace,
      leftClearSpace,
      mentionObstructions,
      buildingNumber,
      approvalNumber,
      dateOfIssue,
      dueDate,
      premiseArea,
      zone,
      block,
      building,
      nearestLandmark,
      floors,
      pinCode,
      city,
    ]
  );

  const isCollapseVisible = useMemo(
    () => fields.some(({ isVisible }) => isVisible),
    [fields]
  );

  return (
    <>
      {isCollapseVisible && (
        <Collapse
          title="General Building Information"
          isExpandable={isExpandable}
        >
          <Row className="d-flex mb-2">
            {fields.map(
              ({
                name,
                isVisible,
                key,
                header = false,
                isRequired,
                renderField,
              }) => {
                const value = get(applicationData, key, "");
                if (typeof isVisible !== "boolean" || isVisible) {
                  return header ? (
                    <Col key={key} lg={12} className="mb-1">
                      <Row>
                        <Col xs={12} className="fw-bold fs-5">
                          {t(name)}
                        </Col>
                      </Row>
                    </Col>
                  ) : (
                    <Col key={key} lg={6} className="mb-1">
                      <Row>
                        <Col md={5}>{t(name)}:</Col>
                        <Col
                          md={7}
                          className="fw-semibold"
                          style={{ wordBreak: "break-word" }}
                        >
                          {renderField
                            ? renderField(value)
                            : get(applicationData, key, null) ??
                              (isRequired ? "-" : "N/A")}
                        </Col>
                      </Row>
                    </Col>
                  );
                }
              }
            )}
          </Row>
        </Collapse>
      )}
    </>
  );
};

GeneralInformationView.propTypes = {
  applicationData: PropTypes.object,
  isExpandable: PropTypes.bool,
  isTracking: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(GeneralInformationView);
